import React, { useState, useEffect } from 'react';

interface SettingsProps {
  onSettingsChange: (settings: {
    useAuth: boolean;
    baseUrl: string;
    customEndpoint?: string;
  }) => void;
}

const DEFAULT_BASE_URL = 'https://api.aws.us-east-1.cerebrium.ai/v4/p-151ed52d/sandra-voice-agent/';
const NOVANT_DEMO_URL = 'https://api.cortex.cerebrium.ai/v4/p-151ed52d/sandra-voice-agent-dev';

export function Settings({ onSettingsChange }: SettingsProps) {
  const [useAuth, setUseAuth] = useState(true);
  const [baseUrl, setBaseUrl] = useState(DEFAULT_BASE_URL);
  const [customEndpoint, setCustomEndpoint] = useState('/stage/sandraVoice');

  useEffect(() => {
    onSettingsChange({ 
      useAuth, 
      baseUrl,
      customEndpoint: customEndpoint || undefined,
    });
  }, [useAuth, baseUrl, customEndpoint, onSettingsChange]);

  const handleBaseUrlChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newBaseUrl = e.target.value;
    setBaseUrl(newBaseUrl);
    
    // Reset custom endpoint when switching away from NOVANT_DEMO_URL
    if (newBaseUrl !== NOVANT_DEMO_URL) {
      setCustomEndpoint('/stage/sandraVoice');
    }
  };

  // Custom Endpoint field should only show for the specific NOVANT_DEMO_URL
  const shouldShowCustomEndpoint = baseUrl === NOVANT_DEMO_URL;

  return (
    <div className="p-6 bg-white rounded-lg shadow-md mb-6">
      <div className="mb-6">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={useAuth}
            onChange={(e) => setUseAuth(e.target.checked)}
            className="form-checkbox h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-gray-700 font-medium">Use Authentication</span>
        </label>
        <p className="text-sm text-gray-500 mt-1 ml-8">Enable if the room requires an authentication token</p>
      </div>
      
      <div className="mb-6">
        <label className="block text-gray-700 mb-2 font-medium">Select Base URL</label>
        <select
          value={baseUrl}
          onChange={handleBaseUrlChange}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        >
          <option value={DEFAULT_BASE_URL}>{DEFAULT_BASE_URL}</option>
          <option value={NOVANT_DEMO_URL}>{NOVANT_DEMO_URL}</option>
        </select>
        <div className="mt-2 text-xs text-gray-500">
          <p>Select the base URL for the API endpoint</p>
        </div>
      </div>

      {shouldShowCustomEndpoint && (
        <div className="mb-6">
          <label className="block text-gray-700 mb-2 font-medium">Custom Endpoint (e.g., /stage/sandraVoice)</label>
          <input
            type="text"
            value={customEndpoint}
            onChange={(e) => setCustomEndpoint(e.target.value)}
            placeholder="/stage/sandraVoice"
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          />
          <div className="mt-2 text-xs text-gray-500">
            <p>Enter the custom endpoint path</p>
          </div>
        </div>
      )}
    </div>
  );
} 